import { useState, useEffect, useRef, useCallback } from 'react';
import { Search, ArrowRight, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useWorkspaceStore } from '@/stores/workspace.store';
import { useAuthStore } from '@/stores/auth.store';
import { api } from '@/utils/api';
import { toast } from '@/components/ui/Toast';

interface CommandItem {
  id: string;
  label: string;
  description?: string;
  action: () => void;
}

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { workspaces } = useWorkspaceStore();
  const user = useAuthStore((s) => s.user);

  // Open on Cmd+K
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(prev => !prev);
        setQuery('');
        setSelectedIndex(0);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  const items: CommandItem[] = [];

  // Navigation items
  items.push({ id: 'nav-dashboard', label: 'Dashboard', description: 'Zum Dashboard', action: () => navigate('/dashboard') });
  items.push({ id: 'nav-my-day', label: 'Mein Tag', description: 'Tagesübersicht', action: () => navigate('/my-day') });
  items.push({ id: 'nav-my-tasks', label: 'Meine Aufgaben', description: 'Alle zugewiesenen Aufgaben', action: () => navigate('/my-tasks') });
  items.push({ id: 'nav-bookmarks', label: 'Lesezeichen', description: 'Gespeicherte Aufgaben', action: () => navigate('/bookmarks') });

  if (user?.is_admin === 1) {
    items.push({ id: 'nav-admin', label: 'Admin', description: 'Administration', action: () => navigate('/admin') });
  }

  for (const ws of workspaces) {
    items.push({ id: `ws-${ws.id}`, label: ws.name, description: 'Workspace', action: () => navigate(`/w/${ws.id}`) });
    items.push({ id: `ws-${ws.id}-activity`, label: `${ws.name} — Aktivitäten`, description: 'Workspace-Aktivitäten', action: () => navigate(`/w/${ws.id}/activity`) });
    items.push({ id: `ws-${ws.id}-archive`, label: `${ws.name} — Archiv`, description: 'Archivierte Projekte', action: () => navigate(`/w/${ws.id}/archive`) });
  }

  // Quick-add: if query starts with + create a task
  const isQuickAdd = query.startsWith('+') && query.length > 1;
  const quickAddTitle = isQuickAdd ? query.slice(1).trim() : '';

  const handleQuickAdd = useCallback(async () => {
    if (!quickAddTitle || workspaces.length === 0) return;
    const ws = workspaces[0];
    try {
      const projRes = await api.get<{ projects: { id: number; name: string }[] }>(`/workspaces/${ws.id}/projects`);
      if (projRes.projects.length === 0) { toast('Kein Projekt vorhanden', 'error'); return; }
      const proj = projRes.projects[0];
      const secRes = await api.get<{ sections: { id: number }[] }>(`/workspaces/${ws.id}/projects/${proj.id}/sections`);
      if (secRes.sections.length === 0) return;
      await api.post(`/workspaces/${ws.id}/projects/${proj.id}/tasks`, { section_id: secRes.sections[0].id, title: quickAddTitle });
      toast(`"${quickAddTitle}" erstellt`, 'success');
      setOpen(false); setQuery('');
    } catch { toast('Fehler beim Erstellen', 'error'); }
  }, [quickAddTitle, workspaces]);

  const filtered = query.trim() && !isQuickAdd
    ? items.filter(item =>
        item.label.toLowerCase().includes(query.toLowerCase()) ||
        (item.description && item.description.toLowerCase().includes(query.toLowerCase()))
      )
    : isQuickAdd ? [] : items;

  const handleSelect = useCallback((item: CommandItem) => {
    item.action();
    setOpen(false);
    setQuery('');
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(i => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      if (isQuickAdd && quickAddTitle) { handleQuickAdd(); }
      else if (filtered[selectedIndex]) { handleSelect(filtered[selectedIndex]); }
    }
  };

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-[60]" onClick={() => setOpen(false)} />
      <div className="fixed top-[20%] left-1/2 -translate-x-1/2 w-full max-w-md bg-card border border-default rounded-xl shadow-surface-lg z-[61] overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-default">
          <Search className="w-4 h-4 text-dim shrink-0" />
          <input
            ref={inputRef}
            className="flex-1 bg-transparent text-sm text-default placeholder:text-dim outline-none"
            placeholder="Suche oder Aktion..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <kbd className="text-xs text-dim bg-hover px-1.5 py-0.5 rounded">ESC</kbd>
        </div>

        <div className="max-h-64 overflow-y-auto p-1">
          {isQuickAdd && quickAddTitle && (
            <button
              onClick={handleQuickAdd}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm bg-accent/10 text-accent hover:bg-accent/20 transition-colors"
            >
              <Plus className="w-3.5 h-3.5 shrink-0" />
              <span className="flex-1 text-left">Aufgabe erstellen: &quot;{quickAddTitle}&quot;</span>
              <kbd className="text-[10px] bg-hover px-1 py-0.5 rounded">Enter</kbd>
            </button>
          )}
          {filtered.length === 0 && !isQuickAdd && (
            <p className="text-sm text-dim p-3">Keine Ergebnisse — tippe <kbd className="bg-hover px-1 rounded">+</kbd> für Schnellerfassung</p>
          )}
          {filtered.map((item, i) => (
            <button
              key={item.id}
              onClick={() => handleSelect(item)}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                i === selectedIndex ? 'bg-hover text-default' : 'text-muted hover:bg-hover hover:text-default'
              }`}
            >
              <ArrowRight className="w-3.5 h-3.5 shrink-0 text-dim" />
              <span className="flex-1 text-left">{item.label}</span>
              {item.description && <span className="text-xs text-dim">{item.description}</span>}
            </button>
          ))}
        </div>
      </div>
    </>
  );
}
